"use client";

import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { useFetchCandidates } from "@/hooks/dao/election/useFetchCandidates";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { CandidatesEmptyState } from "./CandidatesEmptyState";
import { CandidatesSkeleton } from "./CandidatesSkeleton";
import { ElectionRowsList } from "./ElectionRowsList";

export const CandidatesList: React.FC = () => {
  const t = useTranslations("dao");
  const tCommon = useTranslations("common");
  const { cycle } = useCurrentCycle();
  const { data, isLoading, isError, refetch } = useFetchCandidates(cycle);

  if (isLoading) return <CandidatesSkeleton />;

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <p className="text-sm text-semantic-error">
          {t("election.candidates.loadError")}
        </p>
        <Button
          variant="light"
          colors="accent"
          size="sm"
          onClick={() => refetch()}
        >
          {tCommon("retry")}
        </Button>
      </div>
    );
  }

  // B2 fix — previously we filtered out rows with no display identity to hide
  // the brief race window where chain has a candidate but backend insert
  // hasn't completed. That filter ALSO permanently hid candidates whose
  // backend POST failed (the original "on-chain but invisible" bug). Chain is
  // authoritative for who's a candidate, so we render every chain row;
  // ElectionRow's displayName cascade falls through to the wallet address
  // (via WalletAddress) when nickname is null, giving the submitter a
  // visible row + a vote button as soon as their tx confirms.
  const visible = data ?? [];

  if (visible.length === 0) {
    return <CandidatesEmptyState />;
  }

  return <ElectionRowsList key={cycle} candidates={visible} />;
};
